import { useNavigate } from '@tanstack/react-router';
import { KeyRound } from 'lucide-react';
import { type FormEvent, type JSX, useState } from 'react';
import { supabase } from '../lib/supabase';
import { AuthScreen } from '@/components/auth-screen';
import { Callout } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';

export function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [passkeyError, setPasskeyError] = useState<string | null>(null);

  const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
    setBusy(false);
    if (signInError) {
      setError(signInError.message);
      return;
    }
    // Every password sign-in goes through the passkey offer (skipped if one exists).
    await navigate({ to: '/proposer-passkey' });
  };

  const signInWithPasskey = async (): Promise<void> => {
    setPasskeyBusy(true);
    setPasskeyError(null);
    const { error } = await supabase.auth.signInWithPasskey();
    setPasskeyBusy(false);
    if (error) {
      setPasskeyError(error.message);
      return;
    }
    await navigate({ to: '/' });
  };

  return (
    <AuthScreen>
      <Card>
        <CardContent className="grid gap-5 p-6">
          <h1 className="m-0 text-title">Connexion opérateur</h1>

          <div className="grid gap-3">
            <Button
              type="button"
              size="lg"
              className="w-full"
              onClick={() => void signInWithPasskey()}
              disabled={passkeyBusy}
            >
              <KeyRound aria-hidden="true" />
              {passkeyBusy ? 'Connexion…' : 'Se connecter avec une passkey'}
            </Button>
            {passkeyError !== null && <Callout variant="destructive">{passkeyError}</Callout>}
          </div>

          <div className="flex items-center gap-3 text-caption text-muted-foreground">
            <span aria-hidden="true" className="h-px flex-1 bg-border" />
            ou par mot de passe
            <span aria-hidden="true" className="h-px flex-1 bg-border" />
          </div>

          <form onSubmit={(e) => void submit(e)} className="grid gap-4">
            <Field label="E-mail">
              <Input
                type="email"
                required
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                }}
                placeholder="email@exemple.fr"
                autoComplete="username"
              />
            </Field>
            <Field label="Mot de passe">
              <Input
                type="password"
                required
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                }}
                autoComplete="current-password"
              />
            </Field>
            <Button type="submit" variant="outline" size="lg" disabled={busy} className="w-full">
              {busy ? 'Connexion…' : 'Se connecter'}
            </Button>
          </form>

          {error !== null && <Callout variant="destructive">{error}</Callout>}
        </CardContent>
      </Card>
    </AuthScreen>
  );
}
